import type { OrganizationProfile, UpdateOrganizationProfileInput } from "@closer/shared";
import { TENANT_TYPE_NAMES } from "@closer/shared";
import { Button, Card, FormField, Input, PageHeader, Skeleton, useToast } from "@closer/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { ActivityFeed } from "../../components/ActivityFeed";
import { StateView } from "../../components/StateView";
import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";
import { LeaveOrganizationSection } from "./LeaveOrganizationSection";
import { OrganizationLifecycleCard } from "./OrganizationLifecycleCard";
import { OrganizationTypeCorrectionCard } from "./OrganizationTypeCorrectionCard";

/**
 * Organizasyon Profili (CLOS-188 `[050][S02]`).
 *
 * ### ⚠ Alan matrisi SUNUCUDAN
 *
 * Hangi alanların görüneceğini ve hangilerinin yüksek etkili olduğunu sayfa BİLMİYOR;
 * ikisi de yanıtta geliyor (`editableFields`, `highImpactFields`, `registryLabel`).
 * İstemci kendi listesini tutsaydı, hukuk bürosuna vergi numarası alanı çizmek bir
 * `if` unutmaya bakardı — kartın *"karşı türün alanları GÖSTERİLMEZ"* kriteri bir
 * kural değil, veri.
 *
 * ### Eşzamanlılık görünür
 *
 * Okunan `version` forma gizli olarak taşınıyor ve kaydetmede geri gönderiliyor. Sunucu
 * reddederse (409) kullanıcıya *"başkası güncelledi"* deniyor ve YAZDIKLARI SİLİNMİYOR —
 * formu sıfırlamak, kullanıcının emeğini eşzamanlılık uğruna çöpe atmak olurdu.
 */

const ALAN_ETIKETLERI: Record<string, string> = {
  name: "Görünen ad",
  legalName: "Resmî unvan",
  taxNumber: "Vergi numarası",
  /** Yedek: kanonik etiket sunucudan (`registryLabel`) geliyor, bu yalnız güvenlik ağı. */
  registryNumber: "Sicil numarası",
  country: "Ülke kodu",
  phone: "Telefon",
  email: "E-posta",
  website: "İnternet sitesi",
  addressLine: "Adres",
  logoUrl: "Logo adresi",
};

/**
 * ⚠ ALANIN KURALI, HATASINDAN ÖNCE (kullanıcı bildirimi, 26 Ağustos).
 *
 * Ülke alanı yalnız "Ülke kodu" diyordu; kullanıcı "Türkiye" yazıyor ve iki harf
 * istendiğini ancak kaydettikten sonra, kırmızı bir mesajla öğreniyordu. Kural artık
 * alanın altında, daha yazmadan duruyor. Biçimi olan başka bir alan çıkarsa yeri burası.
 */
const ALAN_IPUCLARI: Record<string, { hint: string; placeholder: string }> = {
  country: {
    hint: "İki harfli ülke kodu (ISO 3166-1): TR, DE, US...",
    placeholder: "TR",
  },
};

/** Metin alanı sırası — kart bir yerleşim dayatmıyor (açık karar: teknik seçim). */
const SIRA = [
  "name",
  "legalName",
  "taxNumber",
  "registryNumber",
  "country",
  "phone",
  "email",
  "website",
  "addressLine",
  "logoUrl",
] as const;

type Taslak = Record<string, string>;

function taslagaCevir(profil: OrganizationProfile): Taslak {
  const taslak: Taslak = {};
  for (const alan of profil.editableFields) {
    taslak[alan] = (profil as unknown as Record<string, string | null>)[alan] ?? "";
  }
  return taslak;
}

export function OrganizationProfilePage() {
  const { activeTenantId, activeTenant, can } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();

  const sorgu = useQuery({
    queryKey: activeTenantId
      ? tenantKey(activeTenantId, "organization-profile")
      : ["t", "yok", "organization-profile"],
    enabled: activeTenantId !== null,
    queryFn: async () =>
      (
        await api<OrganizationProfile>(`/api/tenants/${activeTenantId!}/organization-profile`, {
          tenantId: activeTenantId!,
        })
      ).data,
  });

  const [taslak, setTaslak] = useState<Taslak | null>(null);
  const [gerekce, setGerekce] = useState("");
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [alanHatalari, setAlanHatalari] = useState<Record<string, string>>({});

  /** Sunucudan gelen profil taslağı BESLİYOR; kullanıcı yazmaya başladıysa ezmiyor. */
  useEffect(() => {
    if (sorgu.data && taslak === null) setTaslak(taslagaCevir(sorgu.data));
  }, [sorgu.data, taslak]);

  if (sorgu.isPending) {
    return (
      <div role="status" aria-label="Organizasyon profili yükleniyor" className="w-full max-w-2xl">
        <Skeleton lines={6} />
      </div>
    );
  }

  if (sorgu.error || !sorgu.data || !taslak) {
    return (
      <div className="max-w-2xl">
        <StateView
          state={resolveErrorState(sorgu.error, { idempotent: true, existenceIsSecret: false })}
          onRetry={() => void sorgu.refetch()}
        />
      </div>
    );
  }

  const profil = sorgu.data;
  /**
   * ⚠ Kapalı organizasyonda düzenleme SUNULMUYOR (CLOS-195 `[054]`). Gerçek kapı
   * `requireTenant`'taki yazma yasağı; buradaki hesap kullanıcıyı 403 duvarına
   * toslatmamak için.
   */
  const kapali = profil.operationalStatus === "closed";
  /**
   * ⚠ ENGEL ÖNDEN (CLOS-1192). Okuma kapısı `organization.view`, yazma kapısı
   * `organization.profile` (`modules/tenants/routes.ts`). Sayfa yalnız okuma yetkisi olan
   * üyeye de açılıyor — bu bilinçli, üye kendi organizasyonunun resmî bilgilerini
   * görebilmeli — ama form yazılabilir çizildiği sürece kullanıcı kesin bir reddi almaya
   * gönderiliyordu: Kaydet açıktı, sunucu 403 veriyordu, hiçbir şey değişmiyordu.
   */
  const yazabilir = can("organization.profile");
  const yuksekEtkili = new Set(profil.highImpactFields);
  const gorunen = SIRA.filter((alan) => profil.editableFields.includes(alan));

  /** Yüksek etkili bir alan gerçekten DEĞİŞTİ mi — gerekçe alanı ona göre çıkıyor. */
  const degisenYuksekEtkili = gorunen.filter((alan) => {
    if (!yuksekEtkili.has(alan)) return false;
    const mevcut = (profil as unknown as Record<string, string | null>)[alan] ?? "";
    return taslak[alan] !== mevcut;
  });

  async function kaydet() {
    if (!taslak) return;
    setKaydediliyor(true);
    setAlanHatalari({});
    try {
      const govde: Record<string, unknown> = { version: profil.version };
      for (const alan of gorunen) {
        const mevcut = (profil as unknown as Record<string, string | null>)[alan] ?? "";
        if (taslak[alan] !== mevcut) {
          /**
           * Boş metin `null` demek: "sildim" ile "dokunmadım" farklı şeyler.
           *
           * ⚠ `name` MUAF (kusur avı, 26 Ağustos). Şemada nullable değil; boşaltılınca
           * `null` gidiyor ve sunucu kutunun altına İngilizce bir tip hatası basıyordu
           * ("expected string, received null"). Boş metin olduğu gibi gidince şemanın bu
           * iş için yazılmış Türkçe mesajı ("Ad en az 2 karakter olmalıdır") çalışıyor.
           */
          govde[alan] = taslak[alan] === "" && alan !== "name" ? null : taslak[alan];
        }
      }
      if (degisenYuksekEtkili.length > 0) govde.justification = gerekce;

      const { data } = await api<OrganizationProfile>(
        `/api/tenants/${activeTenantId!}/organization-profile`,
        {
          method: "PATCH",
          body: JSON.stringify(govde as UpdateOrganizationProfileInput),
          tenantId: activeTenantId!,
        },
      );
      queryClient.setQueryData(tenantKey(activeTenantId!, "organization-profile"), data);
      setTaslak(taslagaCevir(data));
      setGerekce("");
      toast.success("Organizasyon profili güncellendi");
    } catch (error) {
      if (error instanceof ApiRequestError && error.fields) {
        setAlanHatalari(Object.fromEntries(error.fields.map((f) => [f.field, f.message])));
      }
      /**
       * ⚠ Taslak SIFIRLANMIYOR — 409 dahil. Kartın kriteri: *"kaydetme başarısızsa eski
       * değerler korunur; sahte başarı ... olmaz."* Formu temizlemek, kullanıcının
       * emeğini eşzamanlılık uğruna çöpe atmak olurdu; sunucudaki veri zaten değişmedi.
       */
      toast.error(
        error instanceof ApiRequestError ? error.message : "Kaydedilemedi. Lütfen tekrar deneyin.",
      );
    } finally {
      setKaydediliyor(false);
    }
  }

  return (
    <div className="flex w-full flex-col gap-4">
      <PageHeader
        title="Organizasyon Profili"
        description="Resmî kayıt bilgilerini görüntüleyin ve gerektiğinde güncelleyin."
      />

      {/*
        ⚠ DAR SÜTUN BAŞLIĞIN ALTINDA, SAYFANIN TAMAMINDA DEĞİL (5 Eylül denetimi · C1).

        Bu blok bir FORM: 1400px'e yayılan bir metin girdisi okunmaz. Ama daraltma
        kapsayıcının kendisine uygulandığında `PageHeader` de daralıyordu ve başlık, kendi
        sekme şeridiyle hizasız kalıyordu — Organizasyon Yönetimi'nin yedi sekmesinde
        ölçüldü: içerik bloğunun sol kenarı 281px ile 413px arasında oynuyor, sekme şeridi
        281'de sabit duruyordu.

        Kapsayıcı genişliği artık KABUĞUN işi (`AppLayout`, `max-w-[1440px]`); darlık
        yalnız onu gerçekten isteyen içeriğe uygulanıyor.
      */}
      <div className="flex w-full max-w-2xl flex-col gap-4">
        {/*
        ACIK: spec §7.11 bu sayfa için "DefinitionGrid kartları" öneriyor, ama profil
        DÜZENLENEBİLİR bir form — DefinitionGrid salt-okunur anahtar/değer gösterimi için
        tasarlandı (bkz. ClientDetailPage kullanımı). Form alanlarını DefinitionGrid'e
        zorlamak veri kaybı riski taşır; koordinatörden netleşme bekleniyor, bu tren yalnız
        §6 iskelet zorunluluğunu (PageHeader) karşılıyor.
      */}
        {can("organization.lifecycle") ? (
          <OrganizationLifecycleCard tenantId={activeTenantId!} status={profil.operationalStatus} />
        ) : null}

        {/*
        Tür düzeltmesi PROFİL FORMUNUN DIŞINDA ve kendi yetkisiyle (CLOS-812). Kişisel ve
        müvekkil alanlarında hiç çizilmiyor: orada düzeltilecek bir "organizasyon türü" yok.
      */}
        {can("organization.type_correction") &&
        (profil.type === "enterprise_company" || profil.type === "enterprise_law_firm") ? (
          <OrganizationTypeCorrectionCard tenantId={activeTenantId!} currentType={profil.type} />
        ) : null}

        <Card title="Organizasyon Profili">
          <div className="flex flex-col gap-4">
            {/* Tür DEĞİŞMEZ (`[023]`): gösteriliyor ama düzenlenmiyor. */}
            <p className="text-sm text-fg-muted">
              Tür: <span className="font-medium text-fg">{TENANT_TYPE_NAMES[profil.type]}</span>
              {activeTenant ? <span className="text-fg-soft"> · {activeTenant.name}</span> : null}
            </p>

            <form
              noValidate
              className="flex flex-col gap-4"
              onSubmit={(event) => {
                event.preventDefault();
                void kaydet();
              }}
            >
              {gorunen.map((alan) => (
                <FormField
                  key={alan}
                  label={alan === "registryNumber" ? profil.registryLabel : ALAN_ETIKETLERI[alan]!}
                  error={alanHatalari[alan]}
                  hint={ALAN_IPUCLARI[alan]?.hint}
                >
                  <Input
                    placeholder={ALAN_IPUCLARI[alan]?.placeholder}
                    /**
                     * ⚠ `readOnly`, `disabled` DEĞİL: devre dışı girdi odak almaz ve ekran
                     * okuyucu çoğu tarayıcıda içeriğini atlar. Okuma kasıtlı olarak açık
                     * olduğuna göre değer okunabilir, seçilebilir ve kopyalanabilir kalmalı.
                     */
                    readOnly={!yazabilir}
                    value={taslak[alan] ?? ""}
                    onChange={(event) =>
                      setTaslak((onceki) => ({ ...onceki!, [alan]: event.target.value }))
                    }
                  />
                </FormField>
              ))}

              {/*
              ⚠ Gerekçe alanı YALNIZ yüksek etkili bir alan gerçekten değiştiğinde çıkıyor.
              Her kayıtta sormak, kullanıcıyı anlamsız bir alana alıştırır ve gerekçe
              "güncelleme" gibi boş cümlelere dönerdi.
            */}
              {degisenYuksekEtkili.length > 0 ? (
                <FormField
                  label="Değişiklik gerekçesi"
                  error={alanHatalari.justification}
                  required
                  hint={`Resmî bilgi değişiyor: ${degisenYuksekEtkili
                    .map((a) =>
                      a === "registryNumber" ? profil.registryLabel : ALAN_ETIKETLERI[a],
                    )
                    .join(", ")}`}
                >
                  <Input value={gerekce} onChange={(event) => setGerekce(event.target.value)} />
                </FormField>
              ) : null}

              {!yazabilir ? (
                /**
                 * ⚠ Yetki engeli ÖNCE okunuyor: kapalı bir organizasyonda yetkisiz üyeye
                 * "önce yeniden etkinleştirin" demek, yapamayacağı bir işi tarif etmek olurdu.
                 */
                <p className="text-sm text-fg-muted">
                  Profil bilgilerini görüntüleyebilirsiniz; düzenleme yetkiniz yok.
                </p>
              ) : kapali ? (
                <p className="text-sm text-warning">
                  Organizasyon kapalı: profil düzenlenemez. Düzenlemek için önce yeniden
                  etkinleştirin.
                </p>
              ) : (
                <div className="flex justify-end">
                  <Button type="submit" loading={kaydediliyor}>
                    Kaydet
                  </Button>
                </div>
              )}
            </form>
          </div>
        </Card>

        {/*
        Aktivite AYRI BİR SAYFA DEĞİL (CLOS-283 `[055]`): ilgili kaydın bağlamında
        duruyor. Sidebar'a bir "Aktivite" öğesi eklemek kartın açık yasağı olurdu.
      */}
        <ActivityFeed entityType="tenant" entityId={profil.tenantId} />

        {/*
        Ayrılma (CLOS-78 `[041]`) profil sayfasının SONUNDA: herkesin yapabileceği ve geri
        alınamaz bir işlem, ekranın en görünür yerinde durmamalı.
      */}
        <LeaveOrganizationSection />
      </div>
    </div>
  );
}
